/**
 * MiniProjectGoalCard — visual rendering of a single Mini-Project Goal.
 *
 * Mirrors the Full Project MissionSubKpiCard header pattern (status dot,
 * name + badges, value/target meta row, full-width progress bar) so users
 * carry visual intuition from Mini-Project up to Full Project (Skip's
 * "training ground" principle). Stripped of MissionSubKpiCard's heavier
 * bits: no expand chevron, no history table, no sparkline, no /100 score
 * column.
 *
 * Three-affordance card pattern (DEV-1075 Phase 7.3a-ii, matches the
 * FP ScheduleThermometer convention locked by DEV-841):
 *
 *   - BODY click (any viewer): fires onView. Parent opens the
 *     read-only GoalDetailModal — public-readable surface showing the
 *     TipTap description, photos, target/current/progress, deadline.
 *
 *   - DATABASE icon (Lead-only): fires onDataEntry. Parent opens
 *     GoalDataEntryTray — Lead-only data capture surface for entering
 *     today's value. Hidden for checklist primitives — the inline
 *     toggles below are already the natural data-entry mechanic for
 *     checklists (Skip 2026-05-24 — option (a)).
 *
 *   - PENCIL icon (Lead-only): fires onEdit. Parent opens
 *     GoalEditTray — Lead-only configuration surface for the goal's
 *     definition (name, target, description, photos, etc.).
 *
 * Card body is hover-cursor-clickable; icons stop propagation so they
 * don't double-trigger.
 *
 * Per-primitive rendering:
 *   - count, currency: progress bar with "X of Y unit"
 *   - percentage: progress bar with "X%" (target implicit 100)
 *   - checklist: progress bar with "X of N complete" + togglable
 *     checkbox list of items below (inline toggles are the data-entry
 *     path for checklists; no database icon needed)
 *
 * Color: simple binary — default accent while in-progress, success
 * green when current >= target. No threshold tick marks per Skip's
 * 2026-05-20 call.
 *
 * Pre-Phase-7.3a-ii the card had inline click-to-edit on the current
 * value text. That conflated configuration and data entry on the same
 * surface — separated now per the DEV-841 principle ("pencil = edit
 * settings, NOT edit data"). The database icon is the data path.
 *
 * Template badge regression (Phase 7.1): the pre-DEV-1075 code rendered
 * a "Template" badge when goal.playbookGoalKey was set. The Phase 3
 * backfill didn't carry playbook_goal_key over to kpi_config — there's
 * no column for it. So the badge is removed here. See GoalsSection
 * file-level comment for the full disposition.
 *
 * Strings localized via `pages:miniProject.cards.goal.*`
 * (Chunk D Phase 3 sweep, 2026-05-22).
 */

import React, { useCallback, useMemo, useState } from "react";
import { useAppTranslation } from "../../i18n/useAppTranslation";
import type {
  MiniProjectGoalView,
  DerivedChecklistItem,
} from "../../Data/MiniProjectConfig/miniProjectGoalView";
import type { MiniProjectAssessment } from "../../api/miniProjectStatuses";
import { upsertMissionPoint, todayIso } from "../MiniProjectGoalEdit/upsertMissionPoint";
import styles from "./MiniProjectGoalCard.module.css";

/* ── Types ─────────────────────────────────────────────────────── */

interface Props {
  miniProjectGuid: string;
  goal: MiniProjectGoalView;
  isLead: boolean;
  /** Fired after a value/checklist update succeeds. Parent refetches. */
  onMutated: () => void;
  /** Fired when the Lead clicks the pencil to open the configuration
   *  tray. Lead-only affordance — pencil = "edit settings". */
  onEdit: () => void;
  /** Fired when the Lead clicks the database icon to open the data
   *  entry tray. Lead-only affordance — database = "edit data".
   *  Hidden for checklist primitives (inline toggles handle data). */
  onDataEntry: () => void;
  /** Fired when ANY viewer clicks the card body. Parent opens the
   *  read-only GoalDetailModal showing the description, photos, and
   *  full context. Public-accessible surface. */
  onView: () => void;
  /**
   * Optional. The most recent filed-status assessment for THIS goal
   * (from MiniProjectStatus.goalAssessments). When set, drives the
   * status dot + progress-bar color in place of the
   * complete-vs-in-progress binary. Mapping (locked 2026-05-27):
   *
   *   ahead     → success (green)
   *   on_track  → success (green)
   *   behind    → warning (yellow)
   *   blocked   → danger  (red)
   *   done      → info    (blue) — platform "complete" blue per
   *                                 the Mission heatmap palette
   *
   * When undefined, the card falls back to its prior binary behavior
   * (success when currentValue >= targetValue, default otherwise) so
   * consumers that don't pass an assessment keep working unchanged.
   *
   * Mission sub-tab (#230) is the first consumer; passes the latest
   * status's per-goal assessment so the bar reflects the Lead's most
   * recent qualitative call.
   */
  assessment?: MiniProjectAssessment | null;
}

/** Translate an assessment to the data-state attribute the card's
 *  CSS reads. Centralized so the mapping lives in one place; the CSS
 *  defines the actual colors. */
function assessmentToDataState(
  a: MiniProjectAssessment,
): "success" | "warning" | "danger" | "info" {
  if (a === "ahead" || a === "on_track") return "success";
  if (a === "behind") return "warning";
  if (a === "blocked") return "danger";
  return "info"; // done
}

/* ── Helpers ───────────────────────────────────────────────────── */

function formatVal(v: number | null | undefined, decimals = 2): string {
  if (v === null || v === undefined || !Number.isFinite(v)) return "—";
  return Number.isInteger(v) ? String(v) : v.toFixed(decimals);
}

function formatCurrency(v: number | null | undefined, code: string): string {
  if (v === null || v === undefined || !Number.isFinite(v)) return "—";
  // Use Intl.NumberFormat when the code is a valid ISO 4217 (most are).
  // Fall back to plain number + code suffix if Intl rejects it.
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: code || "USD",
      maximumFractionDigits: 0,
    }).format(v);
  } catch {
    return `${formatVal(v, 0)} ${code}`;
  }
}

// todayIso and upsertMissionPoint moved to
// ../MiniProjectGoalEdit/upsertMissionPoint.ts (Phase 7.3a-ii) so the
// GoalDataEntryTray and this card share retry semantics without
// duplicating the 409→PUT branch logic. Imported above.

/* ── Inline icons (no external deps) ───────────────────────────── */

const PencilIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M12 20h9" />
    <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
  </svg>
);

/** Database / cylinder icon — matches the FP ScheduleThermometer
 *  pattern (DEV-841). Represents the underlying data points, not the
 *  configuration. */
const DatabaseIcon = () => (
  <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor"
    strokeWidth="1.3" aria-hidden="true">
    <ellipse cx="8" cy="3.25" rx="5.25" ry="1.75" />
    <path d="M2.75 3.25v9.5c0 .97 2.35 1.75 5.25 1.75s5.25-.78 5.25-1.75v-9.5" />
    <path d="M2.75 7c0 .97 2.35 1.75 5.25 1.75s5.25-.78 5.25-1.75" />
    <path d="M2.75 10.5c0 .97 2.35 1.75 5.25 1.75s5.25-.78 5.25-1.75" />
  </svg>
);

const CheckIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

/* ── Component ─────────────────────────────────────────────────── */

const MiniProjectGoalCard: React.FC<Props> = ({
  miniProjectGuid,
  goal,
  isLead,
  onMutated,
  onEdit,
  onDataEntry,
  onView,
  assessment,
}) => {
  const { t } = useAppTranslation("pages");
  // Local saving / error state — only used for the inline checklist
  // toggle path. The numeric inline-edit state machine was removed in
  // Phase 7.3a-ii; numeric data entry now goes through the database
  // icon → GoalDataEntryTray.
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isChecklist = goal.primitive === "checklist";

  // Materialized checklist state from the unified view (per-item
  // completion derived from mission_metric_points with item_id set).
  const checklistItems: DerivedChecklistItem[] = useMemo(
    () => goal.derivedChecklist ?? [],
    [goal.derivedChecklist],
  );

  // Compute percentage complete for the progress bar.
  const { progressPct, isComplete } = useMemo(() => {
    if (goal.primitive === "percentage") {
      const v = Math.max(0, Math.min(100, goal.currentValue ?? 0));
      return { progressPct: v, isComplete: v >= 100 };
    }
    if (isChecklist) {
      const total = checklistItems.length;
      if (total === 0) return { progressPct: 0, isComplete: false };
      const done = checklistItems.filter((it) => it.checked).length;
      return { progressPct: (done / total) * 100, isComplete: done >= total };
    }
    // count, currency
    const target = goal.targetValue;
    if (!target || target <= 0) return { progressPct: 0, isComplete: false };
    const pct = Math.min(100, ((goal.currentValue ?? 0) / target) * 100);
    return { progressPct: pct, isComplete: (goal.currentValue ?? 0) >= target };
  }, [goal.primitive, goal.currentValue, goal.targetValue, isChecklist, checklistItems]);

  // Checklist toggle: write a single per-item mission_metric_point with
  // item_id='mp_item_<index>'. The goal's currentValue + progress bar
  // both derive from the count of checked items via miniProjectGoalView
  // on the next refetch — no separate aggregate write needed.
  const toggleChecklistItem = useCallback(
    async (item: DerivedChecklistItem) => {
      if (!isLead) return;
      setSaving(true);
      setError(null);
      try {
        await upsertMissionPoint({
          miniProjectGuid,
          subKpiKey: goal.goalGuid,
          reportingDate: todayIso(),
          value: item.checked ? 0 : 1, // toggle
          itemId: item.itemId,
        });
        onMutated();
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      } finally {
        setSaving(false);
      }
    },
    [isLead, miniProjectGuid, goal.goalGuid, onMutated],
  );

  // Status dot + progress-bar color.
  // If an assessment is provided (Mission sub-tab passes the latest
  // filed status's per-goal call), it WINS over the binary
  // complete-vs-in-progress heuristic. That's the whole point: Skip's
  // 2026-05-27 ask is for the bar to reflect the Lead's most recent
  // qualitative judgement, not just whether the number hit the target.
  // Falls back to the prior binary when no assessment is passed.
  const dotColor: "success" | "warning" | "danger" | "info" | "default" =
    assessment != null
      ? assessmentToDataState(assessment)
      : isComplete
        ? "success"
        : "default";

  // Render the value row content per primitive. All variants are
  // display-only post-Phase-7.3a-ii — the click target is the card
  // body (opens GoalDetailModal); the data-entry path is the database
  // icon (opens GoalDataEntryTray).
  const valueRowContent = isChecklist ? (
    <span className={styles.value}>
      {t("miniProject.cards.goal.checklistComplete", {
        done: checklistItems.filter((it) => it.checked).length,
        total: checklistItems.length,
      })}
    </span>
  ) : goal.primitive === "currency" ? (
    <>
      <span className={styles.value}>
        {formatCurrency(goal.currentValue ?? 0, goal.currencyCode || "USD")}
      </span>
      {goal.targetValue != null && (
        <span className={styles.target}>
          {t("miniProject.cards.goal.ofCurrency", { target: formatCurrency(goal.targetValue, goal.currencyCode || "USD") })}
        </span>
      )}
    </>
  ) : goal.primitive === "percentage" ? (
    <span className={styles.value}>{formatVal(goal.currentValue ?? 0, 1)}%</span>
  ) : (
    /* count */
    <>
      <span className={styles.value}>{formatVal(goal.currentValue ?? 0)}</span>
      <span className={styles.target}>
        {t("miniProject.cards.goal.of", { target: formatVal(goal.targetValue), unit: goal.unitKey ?? "" })}
      </span>
    </>
  );

  // Card body click → opens detail modal. We attach the handler at the
  // card container; icon buttons inside stopPropagation so they don't
  // double-trigger. Keyboard equivalent (Enter / Space) so it's not a
  // mouse-only affordance.
  const handleCardActivate = useCallback(() => {
    onView();
  }, [onView]);

  return (
    <div
      className={`${styles.card} ${styles.cardClickable}`}
      onClick={handleCardActivate}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          handleCardActivate();
        }
      }}
      aria-label={t("miniProject.cards.goal.cardAria", { name: goal.nameKey })}
    >
      {/* ── Header row: status dot + name + badges + lead actions ── */}
      <div className={styles.headerRow}>
        <span className={styles.statusDot} data-state={dotColor} aria-hidden="true" />
        <div className={styles.titleArea}>
          <span className={styles.name}>{goal.nameKey}</span>
          {/* Template badge removed in Phase 7.1 — see file-level comment. */}
          {goal.required && (
            <span className={`${styles.badge} ${styles.badgeRequired}`}>{t("miniProject.cards.goal.requiredBadge")}</span>
          )}
        </div>
        {isLead && (
          <div className={styles.iconRow}>
            {/* Database icon: data entry. Hidden for checklist primitives
                — inline toggles below are already the natural data path
                for checklists (Skip 2026-05-24 — option (a)). */}
            {!isChecklist && (
              <button
                type="button"
                className={styles.iconBtn}
                onClick={(e) => {
                  e.stopPropagation();
                  onDataEntry();
                }}
                aria-label={t("miniProject.cards.goal.dataEntryAria")}
                title={t("miniProject.cards.goal.dataEntryTitle")}
              >
                <DatabaseIcon />
              </button>
            )}
            {/* Pencil icon: configuration. */}
            <button
              type="button"
              className={styles.iconBtn}
              onClick={(e) => {
                e.stopPropagation();
                onEdit();
              }}
              aria-label={t("miniProject.cards.goal.editAria")}
              title={t("miniProject.cards.goal.editTitle")}
            >
              <PencilIcon />
            </button>
          </div>
        )}
      </div>

      {/* ── Value / target row (display-only) ── */}
      <div className={styles.valueRow}>{valueRowContent}</div>

      {/* ── Progress bar ── */}
      <div className={styles.progressOuter}>
        <div
          className={styles.progressInner}
          data-state={dotColor}
          style={{ inlineSize: `${progressPct}%` }}
        />
      </div>

      {/* ── Checklist items (checklist primitive only) ── */}
      {isChecklist && checklistItems.length > 0 && (
        <ul className={styles.checklist}>
          {checklistItems.map((it) => (
            <li key={it.itemId} className={styles.checklistItem}>
              <button
                type="button"
                className={styles.checkbox}
                data-checked={it.checked}
                onClick={(e) => {
                  e.stopPropagation();
                  toggleChecklistItem(it);
                }}
                disabled={!isLead || saving}
                aria-label={it.checked
                  ? t("miniProject.cards.goal.checkIncomplete", { label: it.label })
                  : t("miniProject.cards.goal.checkComplete", { label: it.label })}
              >
                {it.checked && <CheckIcon />}
              </button>
              <span
                className={it.checked ? styles.checklistLabelDone : styles.checklistLabel}
              >
                {it.label}
              </span>
            </li>
          ))}
        </ul>
      )}

      {error && <div className={styles.error}>{error}</div>}
    </div>
  );
};

export default MiniProjectGoalCard;
